import { useRouterState } from '@tanstack/react-router';
import { useEffect } from 'react';

declare global {
  interface Window {
    dataLayer: any[];
    gtag?: (...args: any[]) => void;
  }
}

function ensureGtagLoaded(measurementId: string) {
  if (!measurementId) return;

  if (!window.dataLayer) {
    window.dataLayer = [];
  }

  if (!window.gtag) {
    // Use canonical function form to preserve the built-in 'arguments' object
    // which GA's loader expects when reading from the dataLayer queue.
    window.gtag = function () {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    };
  }

  // If the script tag is already present, don't add it again
  const existing = document.querySelector(`script[src^="https://www.googletagmanager.com/gtag/js?id="]`);
  if (!existing) {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
    document.head.appendChild(script);
  }

  // Initialize GA without auto page_view to avoid double-counting
  window.gtag('js', new Date());
  window.gtag('config', measurementId, { send_page_view: false });
}

// Route params that are low-cardinality, human-meaningful enums (tabs,
// integration kinds) rather than entity IDs. We keep their literal values so
// GA can still distinguish e.g. the "general" vs "members" settings tab,
// instead of collapsing everything to {tab}.
const READABLE_PARAMS = new Set(['tab', 'integrationType']);

// Build a low-cardinality page path from the matched route template + params
// instead of the live URL. High-cardinality segments — the org name, codebase
// IDs, document IDs, splat file paths, etc. — collapse into placeholders so GA
// aggregates by feature page (e.g. /cb/{codebaseId}/overview) rather than
// recording a distinct page for every entity. Deriving from the route template
// keeps this in sync with the route tree automatically, with no fragile denylist.
export function buildPagePath(routePath: string, params: Record<string, string | undefined>): string {
  const segments = routePath
    .split('/')
    .filter(Boolean)
    .flatMap((segment) => {
      // TanStack pathless layout routes (e.g. _access) appear in routeId but not the URL.
      if (segment.startsWith('_')) return [];
      // Drop the org so GA aggregates across customers rather than per-org.
      if (segment === '$organizationName') return [];
      // Splat / catch-all route segment (e.g. a browsed file path).
      if (segment === '$') return ['*'];
      if (segment.startsWith('$')) {
        const name = segment.slice(1);
        const value = params[name];
        // Keep enum-like params readable; collapse entity IDs to {name}.
        if (READABLE_PARAMS.has(name) && value) return [value];
        return [`{${name}}`];
      }
      return [segment];
    });
  return '/' + segments.join('/');
}

let pageViewTimer: ReturnType<typeof setTimeout> | undefined;

// Debounce so redirect chains (e.g. / → /$org → /$org/library) only
// record a page_view for the final destination.
function trackPageView(measurementId: string, pagePath: string) {
  if (!measurementId || !window.gtag) return;

  clearTimeout(pageViewTimer);
  pageViewTimer = setTimeout(() => {
    if (!window.gtag) return;
    // Path only — the query string here is exclusively high-cardinality IDs
    // (versionId, branchId, …), table/pagination state, and user-typed search
    // text, none of which is a useful GA page dimension, so we omit it.
    const normalized = window.location.origin + pagePath;
    window.gtag('event', 'page_view', {
      page_location: normalized,
      page_title: document.title,
    });
  }, 200);
}

export function GaProvider() {
  const measurementId = import.meta.env.VITE_GOOGLE_ANALYTICS as string | undefined;

  // The leaf match holds the most specific route template + params for the URL.
  const pagePath = useRouterState({
    select: (s) => {
      const leaf = s.matches[s.matches.length - 1];
      if (!leaf) return window.location.pathname;
      return buildPagePath(leaf.fullPath, leaf.params);
    },
  });

  // Initialize once on mount
  useEffect(() => {
    if (measurementId) {
      ensureGtagLoaded(measurementId);
    }
  }, [measurementId]);

  // Track on TanStack Router location changes. pathname covers splat (file)
  // navigations that share a normalized pagePath; query-only changes are
  // intentionally ignored since they don't affect the reported page.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  useEffect(() => {
    if (measurementId) {
      trackPageView(measurementId, pagePath);
    }
    return () => clearTimeout(pageViewTimer);
  }, [measurementId, pagePath, pathname]);

  return null;
}
